import React, { useState, useEffect, useCallback } from "react";
import PageLayout from "../components/PageLayout";
import { theme } from "../theme";
import { TenantsAPI, UsersAPI, ROLE_COLORS } from "../auth";

const Card = ({ children, style = {} }) => (
  <div style={{ background: theme.colors.card, border: `1px solid ${theme.colors.cardBorder}`, borderRadius: 12, padding: 20, marginBottom: 20, ...style }}>
    {children}
  </div>
);

const Label = ({ children }) => (
  <div style={{ fontFamily: theme.fonts.mono, fontSize: 11, color: theme.colors.textLabel, letterSpacing: 1, marginBottom: 5 }}>{children}</div>
);

const Input = ({ label, value, onChange, type = "text", placeholder = "", disabled = false }) => (
  <div style={{ marginBottom: 14 }}>
    <Label>{label}</Label>
    <input
      type={type} value={value} onChange={e => onChange(e.target.value)}
      placeholder={placeholder} disabled={disabled}
      style={{ width: "100%", background: disabled ? theme.colors.inputBgDisabled : theme.colors.inputBg, border: `1px solid ${theme.colors.cardBorder}`, borderRadius: 7, color: disabled ? theme.colors.textMuted : theme.colors.textPrimary, fontFamily: theme.fonts.body, fontSize: 13, padding: "9px 12px", outline: "none" }}
    />
  </div>
);

const Btn = ({ onClick, children, color = theme.colors.accent, danger = false, small = false }) => (
  <button onClick={onClick} style={{
    padding: small ? "4px 10px" : "9px 18px",
    background: danger ? "rgba(255,61,87,0.12)" : `${color}18`,
    border: `1px solid ${danger ? theme.colors.danger : color}44`,
    borderRadius: 7, color: danger ? theme.colors.danger : color,
    fontFamily: theme.fonts.display, fontSize: small ? 9 : 11,
    fontWeight: 700, letterSpacing: 1, cursor: "pointer", whiteSpace: "nowrap",
  }}>{children}</button>
);

const PlanBadge = ({ plan }) => {
  const colors = { enterprise: "#FF8C00", standard: theme.colors.accent, demo: theme.colors.textMuted };
  const c = colors[plan] || theme.colors.textMuted;
  return (
    <span style={{ padding: "2px 8px", borderRadius: 4, background: `${c}18`, border: `1px solid ${c}44`, color: c, fontFamily: theme.fonts.mono, fontSize: 10, fontWeight: 700, textTransform: "uppercase" }}>
      {plan}
    </span>
  );
};

function generatePassword(len = 14) {
  const sets = ["ABCDEFGHJKLMNPQRSTUVWXYZ", "abcdefghjkmnpqrstuvwxyz", "23456789", "!@#$%&*"];
  let pass = sets.map(s => s[Math.floor(Math.random() * s.length)]).join("");
  const all = sets.join("");
  for (let i = pass.length; i < len; i++) pass += all[Math.floor(Math.random() * all.length)];
  return pass.split("").sort(() => Math.random() - 0.5).join("");
}

// ── Main Component ────────────────────────────────────────────────────────────

export default function TenantManagement() {
  const [tenants, setTenants]           = useState([]);
  const [selected, setSelected]         = useState(null); // expanded tenant
  const [tenantUsers, setTenantUsers]   = useState([]);
  const [loading, setLoading]           = useState(true);
  const [toast, setToast]               = useState("");
  const [activeTab, setActiveTab]       = useState("tenants");
  const [showTenantForm, setShowTenantForm] = useState(false);
  const [showUserForm, setShowUserForm]     = useState(false);
  const [deleteConfirm, setDeleteConfirm]   = useState(null);

  const [tenantForm, setTenantForm] = useState({
    id: "", name: "", plan: "standard", seats: 5, contact: "", org_size: "medium", notes: "", expires_at: "", active: true,
  });
  const [userForm, setUserForm] = useState({
    username: "", password: generatePassword(), full_name: "", email: "", role: "analyst", active: true, expires_at: "",
  });

  const notify = (msg) => { setToast(msg); setTimeout(() => setToast(""), 3000); };
  const tf = k => v => setTenantForm(p => ({ ...p, [k]: v }));
  const uf = k => v => setUserForm(p => ({ ...p, [k]: v }));

  const loadTenants = useCallback(async () => {
    setLoading(true);
    try {
      const data = await TenantsAPI.list();
      setTenants(data.tenants || []);
    } catch (e) {
      notify("Failed to load tenants");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadTenants(); }, [loadTenants]);

  const loadTenantUsers = async (tid) => {
    try {
      const data = await UsersAPI.list(tid);
      setTenantUsers(data.users || []);
    } catch {
      setTenantUsers([]);
    }
  };

  const selectTenant = async (t) => {
    if (selected?.id === t.id) { setSelected(null); setTenantUsers([]); return; }
    setSelected(t);
    await loadTenantUsers(t.id);
    setActiveTab("users");
  };

  // ── Tenant CRUD ───────────────────────────────────────────────────────────

  const handleCreateTenant = async () => {
    if (!tenantForm.id.trim() || !tenantForm.name.trim()) return notify("ID and Name are required");
    try {
      await TenantsAPI.create({ ...tenantForm, seats: Number(tenantForm.seats) });
      notify("Company created!");
      setShowTenantForm(false);
      setTenantForm({ id: "", name: "", plan: "standard", seats: 5, contact: "", org_size: "medium", notes: "", expires_at: "", active: true });
      loadTenants();
    } catch (e) {
      notify(e.message || "Failed to create company");
    }
  };

  const handleDeleteTenant = async (t) => {
    try {
      await TenantsAPI.delete(t.id);
      notify("Company deleted");
      setDeleteConfirm(null);
      if (selected?.id === t.id) setSelected(null);
      loadTenants();
    } catch (e) {
      notify(e.message || "Failed to delete");
    }
  };

  const handleRegenerateKey = async (id) => {
    try {
      const res = await TenantsAPI.regenerateKey(id);
      notify(`New license key: ${res.license_key}`);
      loadTenants();
    } catch {
      notify("Failed to regenerate key");
    }
  };

  const handleSeedDemo = async () => {
    try {
      const res = await TenantsAPI.seedDemo();
      notify(`Seeded ${res.inserted?.tenants} tenants, ${res.inserted?.users} users`);
      loadTenants();
    } catch {
      notify("Seed failed");
    }
  };

  // ── User CRUD ─────────────────────────────────────────────────────────────

  const handleCreateUser = async () => {
    if (!selected) return;
    if (!userForm.username.trim()) return notify("Username is required");
    if (!userForm.password || userForm.password.length < 6) return notify("Password must be at least 6 characters");
    try {
      await UsersAPI.create(selected.id, userForm);
      notify("User created!");
      setShowUserForm(false);
      setUserForm({ username: "", password: generatePassword(), full_name: "", email: "", role: "analyst", active: true, expires_at: "" });
      loadTenantUsers(selected.id);
      loadTenants();
    } catch (e) {
      notify(e.message || "Failed to create user");
    }
  };

  const handleDeleteUser = async (u) => {
    try {
      await UsersAPI.delete(selected.id, u.id);
      notify("User deleted");
      loadTenantUsers(selected.id);
      loadTenants();
    } catch (e) {
      notify(e.message || "Failed to delete user");
    }
  };

  const handleResetPassword = async (u) => {
    const newPw = generatePassword();
    try {
      await UsersAPI.resetPassword(selected.id, u.id, newPw);
      notify(`Password reset: ${newPw}`);
    } catch {
      notify("Failed to reset password");
    }
  };

  const handleToggleUser = async (u) => {
    try {
      await UsersAPI.update(selected.id, u.id, { ...u, active: !u.active });
      loadTenantUsers(selected.id);
      notify("Status updated");
    } catch {
      notify("Failed to update");
    }
  };

  // ── Render ────────────────────────────────────────────────────────────────

  const tabs = [
    { id: "tenants", label: `Companies (${tenants.length})` },
    ...(selected ? [{ id: "users", label: `Users — ${selected.name}` }] : []),
  ];

  return (
    <PageLayout title="Tenant Management" subtitle="COMPANIES · LICENSES · SEATS · PLATFORM ADMIN">

      {toast && (
        <div style={{ position: "fixed", top: 20, right: 20, zIndex: 9999, padding: "12px 24px", background: theme.colors.success, borderRadius: 8, color: "#000", fontFamily: theme.fonts.display, fontWeight: 700, fontSize: 12, letterSpacing: 1 }}>
          ✓ {toast}
        </div>
      )}

      {/* Tabs */}
      <div style={{ display: "flex", gap: 4, marginBottom: 20, borderBottom: `1px solid ${theme.colors.cardBorder}`, paddingBottom: 1 }}>
        {tabs.map(tab => (
          <button key={tab.id} onClick={() => setActiveTab(tab.id)} style={{
            padding: "10px 20px", background: "transparent", border: "none",
            borderBottom: activeTab === tab.id ? `2px solid ${theme.colors.accent}` : "2px solid transparent",
            color: activeTab === tab.id ? theme.colors.accent : theme.colors.textMuted,
            fontFamily: theme.fonts.display, fontSize: 11, fontWeight: 700, letterSpacing: 1, cursor: "pointer",
          }}>{tab.label.toUpperCase()}</button>
        ))}
      </div>

      {/* ═══ COMPANIES TAB ═══ */}
      {activeTab === "tenants" && (
        <>
          <Card style={{ display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap" }}>
            <div>
              <div style={{ fontFamily: theme.fonts.display, fontSize: 22, fontWeight: 900 }}>
                <span style={{ color: theme.colors.accent }}>{tenants.length}</span>
                <span style={{ color: theme.colors.textSecondary, fontSize: 13, marginLeft: 8 }}>COMPANIES</span>
              </div>
              <div style={{ fontFamily: theme.fonts.mono, fontSize: 11, color: theme.colors.textMuted, marginTop: 2 }}>
                {tenants.filter(t => t.active).length} active · {tenants.reduce((a, t) => a + (t.seats_used || 0), 0)} seats used
              </div>
            </div>
            <div style={{ flex: 1 }} />
            <Btn onClick={handleSeedDemo} color={theme.colors.textMuted}>SEED DEMO</Btn>
            <Btn onClick={() => setShowTenantForm(!showTenantForm)} color={theme.colors.warning}>
              {showTenantForm ? "✕ CANCEL" : "+ ADD COMPANY"}
            </Btn>
          </Card>

          {showTenantForm && (
            <Card style={{ borderColor: `rgba(255,184,0,0.4)`, background: "rgba(255,184,0,0.04)" }}>
              <div style={{ fontFamily: theme.fonts.display, fontSize: 12, fontWeight: 700, color: theme.colors.warning, letterSpacing: 1, marginBottom: 16 }}>NEW COMPANY</div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
                <Input label="TENANT ID (unique code)" value={tenantForm.id} onChange={tf("id")} placeholder="T004" />
                <Input label="COMPANY NAME" value={tenantForm.name} onChange={tf("name")} placeholder="Acme Corporation" />
                <div style={{ marginBottom: 14 }}>
                  <Label>PLAN</Label>
                  <select value={tenantForm.plan} onChange={e => tf("plan")(e.target.value)} style={{ width: "100%", background: theme.colors.inputBg, border: `1px solid ${theme.colors.cardBorder}`, borderRadius: 7, color: theme.colors.textPrimary, fontFamily: theme.fonts.body, fontSize: 13, padding: "9px 12px", outline: "none" }}>
                    <option value="demo">Demo</option>
                    <option value="standard">Standard</option>
                    <option value="enterprise">Enterprise</option>
                  </select>
                </div>
                <Input label="SEATS (max users)" value={tenantForm.seats} onChange={v => tf("seats")(Number(v))} type="number" placeholder="5" />
                <Input label="CONTACT EMAIL" value={tenantForm.contact} onChange={tf("contact")} placeholder="pmo@company.com" />
                <div style={{ marginBottom: 14 }}>
                  <Label>ORG SIZE</Label>
                  <select value={tenantForm.org_size} onChange={e => tf("org_size")(e.target.value)} style={{ width: "100%", background: theme.colors.inputBg, border: `1px solid ${theme.colors.cardBorder}`, borderRadius: 7, color: theme.colors.textPrimary, fontFamily: theme.fonts.body, fontSize: 13, padding: "9px 12px", outline: "none" }}>
                    <option value="small">Small</option>
                    <option value="medium">Medium</option>
                    <option value="large">Large</option>
                    <option value="enterprise">Enterprise</option>
                  </select>
                </div>
                <Input label="EXPIRES (optional)" value={tenantForm.expires_at} onChange={tf("expires_at")} type="date" />
                <Input label="NOTES (internal)" value={tenantForm.notes} onChange={tf("notes")} placeholder="Prospect from Dubai — 30 day trial" />
              </div>
              <Btn onClick={handleCreateTenant} color={theme.colors.warning}>CREATE COMPANY</Btn>
            </Card>
          )}

          {loading ? (
            <Card style={{ textAlign: "center", padding: 40 }}>
              <div style={{ color: theme.colors.textMuted, fontFamily: theme.fonts.mono, fontSize: 12 }}>Loading companies...</div>
            </Card>
          ) : (
            <Card style={{ padding: 0, overflow: "hidden" }}>
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    <tr style={{ background: theme.colors.inputBg }}>
                      {["Company", "Plan", "Seats", "Contact", "Status", "License Key", "Actions"].map(h => (
                        <th key={h} style={{ padding: "11px 14px", textAlign: "left", fontFamily: theme.fonts.mono, fontSize: 11, color: theme.colors.textLabel, borderBottom: `1px solid ${theme.colors.cardBorder}`, whiteSpace: "nowrap", letterSpacing: 1 }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {tenants.map((t, i) => {
                      const expired = t.expires_at && new Date(t.expires_at) < new Date();
                      const isSelected = selected?.id === t.id;
                      return (
                        <tr key={t.id} style={{ borderBottom: `1px solid ${theme.colors.cardBorder}22`, background: isSelected ? `${theme.colors.accent}0A` : expired ? "rgba(255,61,87,0.04)" : i % 2 === 0 ? theme.colors.rowAlt : "transparent", cursor: "pointer" }}
                          onClick={() => selectTenant(t)}>
                          <td style={{ padding: "12px 14px" }}>
                            <div style={{ fontFamily: theme.fonts.body, fontWeight: 700, fontSize: 14, color: theme.colors.textPrimary }}>{t.name}</div>
                            <div style={{ fontFamily: theme.fonts.mono, fontSize: 10, color: theme.colors.textMuted }}>{t.id} · {t.org_size}</div>
                          </td>
                          <td style={{ padding: "12px 14px" }}><PlanBadge plan={t.plan} /></td>
                          <td style={{ padding: "12px 14px" }}>
                            <div style={{ fontFamily: theme.fonts.mono, fontSize: 13, color: (t.seats_used >= t.seats) ? theme.colors.danger : theme.colors.success }}>
                              {t.seats_used} / {t.seats}
                            </div>
                          </td>
                          <td style={{ padding: "12px 14px", fontFamily: theme.fonts.mono, fontSize: 11, color: theme.colors.textMuted }}>{t.contact || "—"}</td>
                          <td style={{ padding: "12px 14px" }}>
                            <span style={{ padding: "2px 8px", borderRadius: 4, background: t.active && !expired ? "rgba(0,230,118,0.1)" : "rgba(255,61,87,0.1)", border: `1px solid ${t.active && !expired ? "rgba(0,230,118,0.3)" : "rgba(255,61,87,0.3)"}`, color: t.active && !expired ? theme.colors.success : theme.colors.danger, fontFamily: theme.fonts.mono, fontSize: 9, fontWeight: 700 }}>
                              {expired ? "EXPIRED" : t.active ? "ACTIVE" : "INACTIVE"}
                            </span>
                          </td>
                          <td style={{ padding: "12px 14px", fontFamily: theme.fonts.mono, fontSize: 10, color: theme.colors.textMuted }} onClick={e => e.stopPropagation()}>
                            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                              <span style={{ maxWidth: 120, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{t.license_key?.slice(0, 8)}…</span>
                              <Btn small onClick={() => handleRegenerateKey(t.id)} color={theme.colors.textMuted}>REGEN</Btn>
                            </div>
                          </td>
                          <td style={{ padding: "12px 14px" }} onClick={e => e.stopPropagation()}>
                            <div style={{ display: "flex", gap: 5 }}>
                              <Btn small onClick={() => selectTenant(t)} color={theme.colors.accent}>USERS</Btn>
                              {deleteConfirm === t.id ? (
                                <>
                                  <Btn small danger onClick={() => handleDeleteTenant(t)}>CONFIRM</Btn>
                                  <Btn small onClick={() => setDeleteConfirm(null)} color={theme.colors.textMuted}>✕</Btn>
                                </>
                              ) : (
                                <Btn small danger onClick={() => setDeleteConfirm(t.id)}>DELETE</Btn>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </Card>
          )}
        </>
      )}

      {/* ═══ USERS TAB ═══ */}
      {activeTab === "users" && selected && (
        <>
          <Card style={{ display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap" }}>
            <div>
              <div style={{ fontFamily: theme.fonts.display, fontSize: 18, fontWeight: 900 }}>
                <span style={{ color: theme.colors.accent }}>{tenantUsers.length}</span>
                <span style={{ color: theme.colors.textSecondary, fontSize: 13, marginLeft: 8 }}>USERS — {selected.name.toUpperCase()}</span>
              </div>
              <div style={{ fontFamily: theme.fonts.mono, fontSize: 11, color: theme.colors.textMuted, marginTop: 2 }}>
                {selected.seats_used || tenantUsers.filter(u => u.active).length} / {selected.seats} seats used · Plan: {selected.plan}
              </div>
            </div>
            <div style={{ flex: 1 }} />
            <Btn onClick={() => setShowUserForm(!showUserForm)} color={theme.colors.warning}>
              {showUserForm ? "✕ CANCEL" : "+ ADD USER"}
            </Btn>
          </Card>

          {showUserForm && (
            <Card style={{ borderColor: `rgba(255,184,0,0.4)`, background: "rgba(255,184,0,0.04)" }}>
              <div style={{ fontFamily: theme.fonts.display, fontSize: 12, fontWeight: 700, color: theme.colors.warning, letterSpacing: 1, marginBottom: 16 }}>NEW USER — {selected.name}</div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
                <Input label="USERNAME" value={userForm.username} onChange={uf("username")} placeholder="john.smith" />
                <div style={{ marginBottom: 14 }}>
                  <Label>PASSWORD</Label>
                  <div style={{ display: "flex", gap: 8 }}>
                    <input value={userForm.password} onChange={e => uf("password")(e.target.value)}
                      style={{ flex: 1, background: theme.colors.inputBg, border: `1px solid ${theme.colors.cardBorder}`, borderRadius: 7, color: theme.colors.textPrimary, fontFamily: theme.fonts.mono, fontSize: 13, padding: "9px 12px", outline: "none" }} />
                    <Btn onClick={() => uf("password")(generatePassword())} color={theme.colors.accent}>↻</Btn>
                  </div>
                </div>
                <Input label="FULL NAME" value={userForm.full_name} onChange={uf("full_name")} placeholder="John Smith" />
                <Input label="EMAIL" value={userForm.email} onChange={uf("email")} placeholder="john@company.com" />
                <div style={{ marginBottom: 14 }}>
                  <Label>ROLE</Label>
                  <select value={userForm.role} onChange={e => uf("role")(e.target.value)} style={{ width: "100%", background: theme.colors.inputBg, border: `1px solid ${theme.colors.cardBorder}`, borderRadius: 7, color: theme.colors.textPrimary, fontFamily: theme.fonts.body, fontSize: 13, padding: "9px 12px", outline: "none" }}>
                    <option value="tenant_admin">Tenant Admin — manages company users</option>
                    <option value="analyst">Analyst — all pages, no user mgmt</option>
                    <option value="viewer">Viewer — Dashboard, Alerts, Reports</option>
                    <option value="guest">Guest — limited access</option>
                  </select>
                </div>
                <Input label="EXPIRES (optional)" value={userForm.expires_at} onChange={uf("expires_at")} type="date" />
              </div>
              <Btn onClick={handleCreateUser} color={theme.colors.warning}>CREATE USER</Btn>
            </Card>
          )}

          <Card style={{ padding: 0, overflow: "hidden" }}>
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr style={{ background: theme.colors.inputBg }}>
                    {["User", "Role", "Email", "Expires", "Status", "Created By", "Actions"].map(h => (
                      <th key={h} style={{ padding: "11px 14px", textAlign: "left", fontFamily: theme.fonts.mono, fontSize: 11, color: theme.colors.textLabel, borderBottom: `1px solid ${theme.colors.cardBorder}`, whiteSpace: "nowrap", letterSpacing: 1 }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {tenantUsers.length === 0 ? (
                    <tr><td colSpan={7} style={{ padding: 40, textAlign: "center", color: theme.colors.textMuted, fontFamily: theme.fonts.mono, fontSize: 12 }}>No users yet — add one above</td></tr>
                  ) : tenantUsers.map((u, i) => {
                    const expired = u.expires_at && new Date(u.expires_at) < new Date();
                    const rc = ROLE_COLORS[u.role] || theme.colors.textMuted;
                    return (
                      <tr key={u.id} style={{ borderBottom: `1px solid ${theme.colors.cardBorder}22`, background: i % 2 === 0 ? theme.colors.rowAlt : "transparent" }}>
                        <td style={{ padding: "11px 14px" }}>
                          <div style={{ fontFamily: theme.fonts.body, fontWeight: 700, fontSize: 13, color: theme.colors.textPrimary }}>{u.username}</div>
                          {u.full_name && <div style={{ fontFamily: theme.fonts.mono, fontSize: 10, color: theme.colors.textMuted }}>{u.full_name}</div>}
                        </td>
                        <td style={{ padding: "11px 14px" }}>
                          <span style={{ padding: "2px 8px", borderRadius: 4, background: `${rc}18`, border: `1px solid ${rc}44`, color: rc, fontFamily: theme.fonts.mono, fontSize: 10, fontWeight: 700 }}>{u.role}</span>
                        </td>
                        <td style={{ padding: "11px 14px", fontFamily: theme.fonts.mono, fontSize: 11, color: theme.colors.textMuted }}>{u.email || "—"}</td>
                        <td style={{ padding: "11px 14px", fontFamily: theme.fonts.mono, fontSize: 11, color: expired ? theme.colors.danger : theme.colors.textMuted }}>
                          {u.expires_at ? new Date(u.expires_at).toLocaleDateString() : "No expiry"}
                        </td>
                        <td style={{ padding: "11px 14px" }}>
                          <span style={{ padding: "2px 8px", borderRadius: 4, background: u.active && !expired ? "rgba(0,230,118,0.1)" : "rgba(255,61,87,0.1)", border: `1px solid ${u.active && !expired ? "rgba(0,230,118,0.3)" : "rgba(255,61,87,0.3)"}`, color: u.active && !expired ? theme.colors.success : theme.colors.danger, fontFamily: theme.fonts.mono, fontSize: 9, fontWeight: 700 }}>
                            {expired ? "EXPIRED" : u.active ? "ACTIVE" : "DISABLED"}
                          </span>
                        </td>
                        <td style={{ padding: "11px 14px", fontFamily: theme.fonts.mono, fontSize: 11, color: theme.colors.textMuted }}>{u.created_by || "—"}</td>
                        <td style={{ padding: "11px 14px" }}>
                          <div style={{ display: "flex", gap: 5, flexWrap: "wrap" }}>
                            <Btn small onClick={() => handleToggleUser(u)} color={theme.colors.warning}>{u.active ? "DISABLE" : "ENABLE"}</Btn>
                            <Btn small onClick={() => handleResetPassword(u)} color={theme.colors.accent}>RESET PW</Btn>
                            <Btn small danger onClick={() => handleDeleteUser(u)}>DELETE</Btn>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </Card>
        </>
      )}

      <div style={{ textAlign: "center", marginTop: 24, fontFamily: theme.fonts.mono, fontSize: 10, color: theme.colors.textMuted, paddingBottom: 20 }}>
        NexGen Platform Admin · Multi-Tenant · Hassaan Alrifai · © {new Date().getFullYear()}
      </div>
    </PageLayout>
  );
}
